import { useEffect, useState, useSyncExternalStore } from 'react';
import { authApi } from '../lib/auth';
import { forgetResult, linkDeviceResults, loadResults, type PlayedGame } from '../lib/results';
import { accountNow, getVersion, subscribe } from '../lib/whoami';
import { PanelHead } from './AccountPanels';

const ordinal = (n: number) => {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${suffix}`;
};

/**
 * How this player did in the games they finished: the account's history when signed in (any
 * device), else this browser's. Server rows cannot be removed from here; local ones can.
 */
export function ResultsList({ limit }: { limit?: number } = {}) {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  const accountId = accountNow()?.id ?? null;
  const [local, setLocal] = useState(loadResults);
  const [saved, setSaved] = useState<PlayedGame[] | null>(null);

  useEffect(() => {
    setSaved(null);
    if (!accountId) return undefined;
    let live = true;
    // Link first, so a game played just before signing in is already on the list.
    linkDeviceResults()
      .then(() => authApi.results())
      .then((r) => {
        if (live) setSaved(r.results);
      })
      .catch(() => {
        // Server unreachable: fall back to what this device remembers.
      });
    return () => {
      live = false;
    };
  }, [accountId]);

  const rows = saved ?? local;
  if (rows.length === 0) return null;

  const best = Math.max(...rows.map((r) => r.score));
  const shown = rows.slice(0, limit);

  return (
    <div className="panel col">
      <PanelHead title="My results" total={rows.length} shown={shown.length} to="/games" />
      <ul className="game-list">
        {shown.map((game) => (
          <li key={game.code}>
            <span className="col" style={{ gap: 0, minWidth: 0 }}>
              <strong>{game.title}</strong>
              <span className="faint">
                {new Date(game.finishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' · '}
                {game.players > 1 ? `${ordinal(game.rank)} of ${game.players}` : 'solo'}
                {game.score === best && rows.length > 1 ? ' · personal best' : ''}
              </span>
            </span>
            <span className="spacer" />
            <span className="lb-score">{game.score}</span>
            {saved ? null : (
              <button
                className="btn btn-sm btn-ghost"
                title="Remove from your results"
                aria-label={`Remove ${game.title} from your results`}
                onClick={() => setLocal(forgetResult(game.code))}
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>
      <p className="faint" style={{ margin: 0 }}>
        {saved ? 'Saved to your account.' : 'Remembered on this device.'}
      </p>
    </div>
  );
}
